import { z } from 'zod';

export const LogStreamSchema = z.enum(['out', 'err']);
export type LogStream = z.infer<typeof LogStreamSchema>;

/**
 * pm2 only distinguishes stdout from stderr. The design's info/warn/error chips
 * need a finer grade, so level is inferred from the message text — see
 * `inferLogLevel`.
 */
export const LogLevelSchema = z.enum(['info', 'warn', 'error', 'debug']);
export type LogLevel = z.infer<typeof LogLevelSchema>;

export const LogLineSchema = z.object({
  /** Stable key for list rendering; unique per line. */
  key: z.string(),
  processId: z.number(),
  processName: z.string(),
  stream: LogStreamSchema,
  level: LogLevelSchema,
  message: z.string(),
  /** Epoch ms. */
  timestamp: z.number(),
});
export type LogLine = z.infer<typeof LogLineSchema>;

const WARN_PATTERN = /\b(warn(ing)?|deprecat(ed|ion)|slow|retry|retrying|backoff|throttl)/i;
const ERROR_PATTERN =
  /\b(error|err|fail(ed|ure)?|exception|fatal|crash(ed)?|refused|timed?\s?out|unhandled|rejection|denied|ENOENT|ECONNREFUSED|ETIMEDOUT)\b/i;
const DEBUG_PATTERN = /\b(debug|trace|verbose)\b/i;

/**
 * Grade a raw pm2 log line. stderr defaults to `error` but is downgraded when
 * the text clearly reads as a warning or debug output — many Node libraries
 * write ordinary diagnostics to stderr, and marking all of it `error` would
 * make the app's error filter useless.
 */
export function inferLogLevel(message: string, stream: LogStream): LogLevel {
  if (ERROR_PATTERN.test(message)) return 'error';
  if (WARN_PATTERN.test(message)) return 'warn';
  if (DEBUG_PATTERN.test(message)) return 'debug';
  return stream === 'err' ? 'error' : 'info';
}
